// useFabricEvents.js
import {useEffect, useRef} from "react";
import {fabric} from "fabric";

export const useFabricEvents = ({
                                    canvas,
                                    setSelectedObject,
                                    setSidebarVisible,
                                    setMenuPosition,
                                    setDrawingMode,
                                    panMode,
                                    setViewportInfo
                                    , setTextSettings
                                    , setShapeSettings
                                    , setImageSettings
                                    , isLoadingCanvasRef
                                    , isErasing
                                    , scheduleCanvasSave
                                    , isTextEditingRef
                                    , onObjectMoving
                                    , onObjectModified
                                    , onObjectRemoved
                                    , onConnectorSelection
                                    , onTouchGestureStart
                                }) => {
    const selectedObjectRef = useRef(null);

    useEffect(() => {
        if (!canvas) return;

        const updateMiniMap = () => {
            const vpt = canvas.viewportTransform;
            const zoom = canvas.getZoom();

            // Mise à jour pour React
            setViewportInfo({
                zoom,
                x: -vpt[4],
                y: -vpt[5],
            });
        };

        const updateMenuPosition = (target) => {
            if (!target) return;
            const boundingRect = target.getBoundingRect(true, true);
            const canvasRect = canvas.upperCanvasEl?.getBoundingClientRect();
            setMenuPosition({
                x: (canvasRect?.left || 0) + boundingRect.left + (boundingRect.width / 2),
                y: (canvasRect?.top || 0) + boundingRect.top + boundingRect.height + 12,
            });
        };

        const handleZoom = (event) => {
            if (isLoadingCanvasRef.current) return;

            if (!event.e) return;

            // Le défilement classique reste disponible pour la page. Le zoom du
            // tableau blanc est volontaire : Ctrl/Cmd + molette, comme Miro.
            if (!event.e.ctrlKey && !event.e.metaKey) return;

            // Ne jamais sortir un Textbox de son édition pendant une saisie.
            const activeObject = canvas.getActiveObject();
            if (activeObject?.type === "textbox" && activeObject.isEditing) return;

            event.e.preventDefault();
            event.e.stopPropagation();

            // Variation continue plutôt qu'un saut de 10 % à chaque évènement
            // de molette, particulièrement sensible sur trackpad.
            let zoom = canvas.getZoom() * Math.pow(0.998, event.e.deltaY);
            zoom = Math.min(Math.max(zoom, 0.5), 2.5);
            canvas.zoomToPoint({x: event.e.offsetX, y: event.e.offsetY}, zoom);
            updateMiniMap();
        };

        let isDragging = false;
        let lastPosX = 0;
        let lastPosY = 0;
        const touchGesture = {active: false, distance: 0, midpoint: null, zoom: 1};

        const getTouchMetrics = (touches) => {
            const [first, second] = touches;
            const midpoint = {
                x: (first.clientX + second.clientX) / 2,
                y: (first.clientY + second.clientY) / 2,
            };
            return {
                midpoint,
                distance: Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY),
            };
        };

        const handleTouchStart = (event) => {
            if (event.touches.length !== 2) return;

            // Intercepter la gestuelle avant Fabric évite qu’un second doigt soit
            // interprété comme un dessin, une gomme ou une sélection d’objet.
            event.preventDefault();
            event.stopPropagation();
            const metrics = getTouchMetrics(event.touches);
            touchGesture.active = true;
            touchGesture.distance = Math.max(metrics.distance, 1);
            touchGesture.midpoint = metrics.midpoint;
            touchGesture.zoom = canvas.getZoom();

            onTouchGestureStart?.();
            canvas.discardActiveObject();
            canvas.isDrawingMode = false;
            canvas.selection = false;
            canvas.skipTargetFind = true;
            canvas.defaultCursor = "grab";
            canvas.hoverCursor = "grab";
            setDrawingMode(false);
            updateMiniMap();
            canvas.requestRenderAll();
        };

        const handleTouchMove = (event) => {
            if (!touchGesture.active || event.touches.length !== 2) return;

            event.preventDefault();
            event.stopPropagation();
            const metrics = getTouchMetrics(event.touches);
            const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();
            const zoom = Math.min(Math.max(touchGesture.zoom * (metrics.distance / touchGesture.distance), 0.5), 2.5);
            const point = new fabric.Point(metrics.midpoint.x - canvasRect.left, metrics.midpoint.y - canvasRect.top);
            canvas.zoomToPoint(point, zoom);

            const viewport = canvas.viewportTransform;
            viewport[4] += metrics.midpoint.x - touchGesture.midpoint.x;
            viewport[5] += metrics.midpoint.y - touchGesture.midpoint.y;
            canvas.setViewportTransform(viewport);
            touchGesture.midpoint = metrics.midpoint;
            updateMiniMap();
            canvas.requestRenderAll();
        };

        const handleTouchEnd = (event) => {
            if (!touchGesture.active || event.touches.length >= 2) return;

            event.preventDefault();
            event.stopPropagation();
            touchGesture.active = false;
            canvas.selection = true;
            canvas.skipTargetFind = false;
            canvas.defaultCursor = "default";
            canvas.hoverCursor = "move";
            updateMiniMap();
            canvas.requestRenderAll();
        };

        const handleMouseDown = (event) => {
            if (isErasing) {
                const target = event.target;
                if (target && !(target.lockMovementX && target.lockMovementY)) {
                    canvas.discardActiveObject();
                    canvas.remove(target);
                    canvas.requestRenderAll();
                    setSelectedObject(null);
                    scheduleCanvasSave(80);
                }
                return;
            }

            if (event.e.altKey || panMode) {
                isDragging = true;
                canvas.selection = false;
                lastPosX = event.e.clientX;
                lastPosY = event.e.clientY;
            }
        };

        const handleMouseMove = (event) => {
            if (!isDragging) return;
            const vpt = canvas.viewportTransform;
            vpt[4] += event.e.clientX - lastPosX;
            vpt[5] += event.e.clientY - lastPosY;
            lastPosX = event.e.clientX;
            lastPosY = event.e.clientY;
            canvas.requestRenderAll();
            updateMiniMap();
        };

        const handleMouseUp = () => {
            isDragging = false;
            canvas.selection = !panMode && !isErasing;
        };

        const handleObjectSelected = (event) => {
            if (isLoadingCanvasRef.current) return;

            if (!event.selected || event.selected.length === 0) return;

            // Le mode liaison s'appuie sur le cycle Fabric terminé. Cela évite
            // de ré-entrer dans la sélection pendant que Fabric calcule son
            // objet actif.
            if (event.selected.length === 1 && onConnectorSelection?.(event.selected[0])) {
                return;
            }

            if (event.selected.length > 1) {
                const activeSelection = canvas.getActiveObject();
                selectedObjectRef.current = activeSelection;
                setSelectedObject(activeSelection);
                updateMenuPosition(activeSelection);
                setSidebarVisible(true);
            } else {
                const target = event.selected[0];
                setSelectedObject(target);
                selectedObjectRef.current = target;

                if (target && target.type === "textbox") {
                    setTextSettings({
                        fontSize: target.fontSize,
                        fontWeight: target.fontWeight,
                        fontStyle: target.fontStyle,
                        fontFamily: target.fontFamily,
                        color: target.fill,
                        backgroundColor: target.backgroundColor || "", // ou "" si transparent
                    });
                } else if (["rect", "circle", "triangle", "polygon"].includes(target.type)) {
                    setShapeSettings({
                        type: target.type,
                        customType: target.customType || target.type,
                        fill: target.fill,
                        backgroundColor: target.backgroundColor
                    });
                } else if (target.type === "line") {
                    setShapeSettings({
                        type: "line",
                        customType: "line",
                        fill: target.stroke,
                        backgroundColor: target.backgroundColor
                    });
                } else if (target.type === "image") {
                    setImageSettings({
                        stroke: target.stroke,
                        strokeWidth: target.strokeWidth,
                    });
                } else if (target.type === "path") {
                    if (target.customType === "arrow") {
                        setShapeSettings({
                            type: "arrow",
                            customType: "arrow",
                            fill: target.fill,
                            backgroundColor: target.backgroundColor
                        });
                    }
                }

                updateMenuPosition(target);
                canvas.isDrawingMode = false;
                setDrawingMode(false);
                setSidebarVisible(true);
            }
        };

        const handleSelectionCleared = () => {
            setSelectedObject(null);
            selectedObjectRef.current = null;
            setSidebarVisible(false);
        };

        const handleObjectMoving = (event) => {
            onObjectMoving?.(event.target);
            if (event.target === selectedObjectRef.current) {
                updateMenuPosition(event.target);
            }
        };

        const normalizeStickyNote = (target) => {
            if (target?.customType !== "sticky_note") return;

            const scaledWidth = Math.max(target.minWidth || 180, target.width * (target.scaleX || 1));
            target.set({width: scaledWidth, scaleX: 1, scaleY: 1});
            target.initDimensions?.();
            target.set({height: Math.max(target.height, target.stickyMinHeight || 220)});
            target.setCoords?.();
        };

        const handleModification = (event) => {
            normalizeStickyNote(event.target);
            onObjectModified?.(event.target);
            if (!isTextEditingRef.current) scheduleCanvasSave();
        };

        const handleObjectRemoved = (event) => {
            onObjectRemoved?.(event.target);
            scheduleCanvasSave();
        };

        const handleDoubleClick = (event) => {
            const target = event.target;

            if (target?.type === "textbox" && target.editable !== false) {
                canvas.setActiveObject(target);
                if (!target.isEditing) target.enterEditing();

                if (target.customType === "sticky_note" && target.isPlaceholder) {
                    target.selectAll();
                } else {
                    target.selectionStart = target.text.length;
                    target.selectionEnd = target.text.length;
                }

                target.hiddenTextarea?.focus();
                isTextEditingRef.current = true;
                setSelectedObject(target);
                canvas.requestRenderAll();
                return;
            }

            if (!canvas.getActiveObject()) {
                setSidebarVisible(false);
                setSelectedObject(null);
            }
        };

        const handleTextEditingEntered = () => {
            isTextEditingRef.current = true;
        };

        const handleTextEditingExited = () => {
            isTextEditingRef.current = false;
            scheduleCanvasSave(120);
        };

        const handleTextChanged = (event) => {
            if (event.target?.customType === "sticky_note") {
                event.target.isPlaceholder = false;
                event.target.set({height: Math.max(event.target.height, event.target.stickyMinHeight || 220)});
                event.target.setCoords?.();
            }
            canvas.requestRenderAll();
        };

        const handleObjectAdded = (e) => {
            if (isLoadingCanvasRef.current) return; // ne rien faire si en cours de chargement
            // On ignore les dessins ici, car on les gère dans path:created
            if (e.target?.type !== "path") {
                scheduleCanvasSave();
            }
        };

        const handlePathCreated = () => {
            if (isLoadingCanvasRef.current) return; // ne rien faire si en cours de chargement
            // Attendre un peu que Fabric finalise bien l’objet
            scheduleCanvasSave(200);
        };

        // Liste des événements
        canvas.on("object:added", handleObjectAdded);
        canvas.on("path:created", handlePathCreated);
        canvas.on("object:modified", handleModification);
        canvas.on("object:removed", handleObjectRemoved);

        canvas.on("selection:created", handleObjectSelected);
        canvas.on("selection:updated", handleObjectSelected);
        canvas.on("selection:cleared", handleSelectionCleared);
        canvas.on("object:moving", handleObjectMoving);

        canvas.on("mouse:wheel", handleZoom);
        canvas.on("mouse:down", handleMouseDown);
        canvas.on("mouse:move", handleMouseMove);
        canvas.on("mouse:up", handleMouseUp);
        canvas.on("mouse:dblclick", handleDoubleClick);
        canvas.on("text:editing:entered", handleTextEditingEntered);
        canvas.on("text:editing:exited", handleTextEditingExited);
        canvas.on("text:changed", handleTextChanged);
        canvas.upperCanvasEl.addEventListener("touchstart", handleTouchStart, {capture: true, passive: false});
        canvas.upperCanvasEl.addEventListener("touchmove", handleTouchMove, {capture: true, passive: false});
        canvas.upperCanvasEl.addEventListener("touchend", handleTouchEnd, {capture: true, passive: false});
        canvas.upperCanvasEl.addEventListener("touchcancel", handleTouchEnd, {capture: true, passive: false});

        return () => {
            canvas.off("object:added", handleObjectAdded);
            canvas.off("path:created", handlePathCreated);
            canvas.off("object:modified", handleModification);
            canvas.off("object:removed", handleObjectRemoved);

            canvas.off("selection:created", handleObjectSelected);
            canvas.off("selection:updated", handleObjectSelected);
            canvas.off("selection:cleared", handleSelectionCleared);
            canvas.off("object:moving", handleObjectMoving);

            canvas.off("mouse:wheel", handleZoom);
            canvas.off("mouse:down", handleMouseDown);
            canvas.off("mouse:move", handleMouseMove);
            canvas.off("mouse:up", handleMouseUp);
            canvas.off("mouse:dblclick", handleDoubleClick);
            canvas.off("text:editing:entered", handleTextEditingEntered);
            canvas.off("text:editing:exited", handleTextEditingExited);
            canvas.off("text:changed", handleTextChanged);
            canvas.upperCanvasEl.removeEventListener("touchstart", handleTouchStart, true);
            canvas.upperCanvasEl.removeEventListener("touchmove", handleTouchMove, true);
            canvas.upperCanvasEl.removeEventListener("touchend", handleTouchEnd, true);
            canvas.upperCanvasEl.removeEventListener("touchcancel", handleTouchEnd, true);
        };
    }, [canvas, isErasing, isTextEditingRef, onConnectorSelection, onObjectModified, onObjectMoving, onObjectRemoved, onTouchGestureStart, panMode, scheduleCanvasSave]);
};
